import { inject } from "mobx-react";
import { observer } from "mobx-react-lite";
import React from "react";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory
} from "react-router";
import {
  CreateView,
  IndexView,
  ShowView,
  withBreadCrumbs
} from "lego-gui-lib/lib";
import i18next from "i18next";
import { ParentRoute } from "../../core/model/parent";
import { NetworkOperatorPrefixSchemaColumns } from "./networkOperatorPrefix/schema";
import {
  CreateNetworkOperatorPrefix,
  ShowNetworkOperatorPrefix
} from "./networkOperatorPrefix/views";
import { NetworkOperatorSchema } from "./schema";

export const NetworkOperator = {
  url: "/networkOperator",
  handler: (props: RouteComponentProps<any>) => {
    const baseUrl = props.match.url;
    const title = i18next.t("hns:networkOperator:domain_plural");
    return withBreadCrumbs(
      { to: baseUrl, children: title },
      <>
        <ParentRoute
          path={baseUrl + "/:id"}
          dataSourceName={"networkOperator"}
        />
        <Switch>
          <Route exact={true} path={baseUrl} component={Index} />
          <Route
            path={baseUrl + "/:id/prefixes/create/:prefixId"}
            component={CreateNetworkOperatorPrefix}
          />
          <Route
            path={baseUrl + "/:id/prefixes/create"}
            component={CreateNetworkOperatorPrefix}
          />
          <Route
            path={baseUrl + "/:id/prefixes/:prefixId"}
            component={ShowNetworkOperatorPrefix}
          />
          <Redirect from={baseUrl + "/:id/prefixes"} to={baseUrl + "/:id"} />
          <Route path={baseUrl + "/create"} component={Create} />
          <Route path={baseUrl + "/:id"} component={Show} />
        </Switch>
      </>
    );
  }
};

const injectDataSource = (comp: any) =>
  inject(
    "networkOperator",
    "networkOperatorPrefix",
    "i18n",
    "auth"
  )(observer(comp));

export const Index = injectDataSource(
  ({ networkOperator, i18n, auth, ...rest }: any) => {
    const title = i18n.t("hns:networkOperator:domain", { count: 2 });
    return (
      <IndexView
        dataSource={networkOperator.listDataSource}
        {...rest}
        title={title}
        rowButtons={{ edit: true, delete: true }}
        toolbarButtons={{ delete: true, export: true }}
        columns={NetworkOperatorSchema}
      />
    );
  }
);

export const Create = injectDataSource(
  ({ networkOperator, i18n, auth, match, ...rest }: any) => {
    const title = i18n.t("common:create", {
      domain: "hns:networkOperator:domain"
    });
    const history = useHistory();
    return withBreadCrumbs(
      { to: match.url + "/create", children: title },
      <CreateView
        dataSource={networkOperator.oneDataSource}
        {...rest}
        title={title}
        fields={NetworkOperatorSchema}
        id={match.params}
        goBack={() => history.goBack()}
      />
    );
  }
);

export const Show = injectDataSource(
  ({
    networkOperator,
    networkOperatorPrefix,
    i18n,
    auth,
    match,
    ...rest
  }: any) => {
    const title = i18n.t("common:edit", {
      domain: "hns:networkOperator:domain"
    });
    const indexTitle = i18n.t("hns:networkOperatorPrefix:domain_plural");
    const history = useHistory();

    return withBreadCrumbs(
      { to: match.url + "/edit", children: title },
      <>
        <ShowView
          dataSource={networkOperator.oneDataSource}
          preloadData={false}
          title={title}
          fields={NetworkOperatorSchema}
          id={match.params}
          goBack={() => history.goBack()}
        />
        <IndexView
          baseUrl={"/prefixes"}
          dataSource={networkOperatorPrefix.listDataSource}
          {...rest}
          title={indexTitle}
          columns={NetworkOperatorPrefixSchemaColumns}
        />
      </>
    );
  }
);
